import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from '../../../hooks/useTranslation';
import React from 'react';
import type { Addons, FirestoreTimestamp } from '../../../services/payment.types';

interface AddonDialogProps {
    selectedAddon: {
        title: string;
        key: keyof Addons;
        validUntil: string | FirestoreTimestamp | null;
    } | null;
    onClose: () => void;
    onPurchase: () => void;
    formatValidUntil: (date: string | FirestoreTimestamp | null | undefined) => string;
    calculateNewValidUntil: (currentValidUntil: string | FirestoreTimestamp | null | undefined) => string;
}

export const AddonDialog: React.FC<AddonDialogProps> = ({
    selectedAddon,
    onClose,
    onPurchase,
    formatValidUntil,
    calculateNewValidUntil,
}) => {
    const { t } = useTranslation();
    if (!selectedAddon) return null;

    const isRenewal = !!selectedAddon.validUntil;

    return (
        <Dialog 
            open={selectedAddon !== null} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                textAlign: 'center'
            }}>
                <Typography variant="h6" component="div">
                    {isRenewal 
                        ? t('plans.addonDialog.title', { addon: selectedAddon.title })
                        : t('plans.addonDialog.titleNew', { addon: selectedAddon.title })}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{
                py: 3,
                textAlign: 'center'
            }}>
                {isRenewal ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '100%',
                            mb: 2
                        }}>
                            <Typography variant="subtitle1" color="info.main" sx={{
                                fontWeight: 600,
                                fontSize: '1.1rem'
                            }}>
                                {selectedAddon.validUntil && formatValidUntil(selectedAddon.validUntil)}
                            </Typography>
                            <Typography variant="body1" sx={{ mx: 2 }}>→</Typography>
                            <Typography variant="subtitle1" color="primary.main" sx={{
                                fontWeight: 600,
                                fontSize: '1.1rem'
                            }}>
                                {selectedAddon.validUntil && calculateNewValidUntil(selectedAddon.validUntil)}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('plans.addonDialog.extendInfo')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" gutterBottom>
                            {t('plans.addonDialog.description', { addon: selectedAddon.title })}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {t('plans.addonDialog.features', { addon: selectedAddon.title })}
                        </Typography>
                        <Box component="ul" sx={{ 
                            mt: 1, 
                            pl: 0,
                            listStylePosition: 'inside',
                            textAlign: 'center'
                        }}>
                            {selectedAddon.key === 'portfolioInsights' && (
                                <>
                                    <Typography component="li" variant="body2">{t('plans.addons.portfolioInsights.feature1')}</Typography>
                                    <Typography component="li" variant="body2">{t('plans.addons.portfolioInsights.feature2')}</Typography>
                                </>
                            )}
                        </Box>
                        <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                            {t('plans.addonDialog.validity')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={onClose}>{t('plans.addonDialog.buttons.cancel')}</Button>
                <Button variant="contained" onClick={onPurchase} color="primary">
                    {isRenewal 
                        ? t('plans.addonDialog.buttons.addToOrder')
                        : t('plans.addonDialog.buttons.purchase')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
